import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings } from "@/context/AppSettingsContext";
import { BRAND_NAME } from "@/lib/constants";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

export default function PinPage() {
  const { signInWithPin } = useAuth();
  const { settings } = useAppSettings();

  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [shake, setShake] = useState(false);
  const submittingRef = useRef(false);

  const triggerShake = useCallback(() => {
    setShake(true);
    setTimeout(() => setShake(false), 450);
  }, []);

  const handleDigit = useCallback(
    (digit: string) => {
      setError(null);
      setPin((prev) => (prev.length >= 6 ? prev : prev + digit));
    },
    [],
  );

  const handleClear = useCallback(() => {
    setPin("");
    setError(null);
  }, []);

  const handleBackspace = useCallback(() => {
    setPin((prev) => prev.slice(0, -1));
    setError(null);
  }, []);

  const submit = useCallback(async () => {
    if (submittingRef.current) return;
    if (pin.length !== 6) {
      setError("Ingresa los 6 dígitos de tu PIN");
      triggerShake();
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    const { error: pinError } = await signInWithPin(pin);
    submittingRef.current = false;
    setSubmitting(false);
    if (pinError) {
      setError(pinError);
      triggerShake();
      setPin("");
    }
  }, [pin, signInWithPin, triggerShake]);

  useEffect(() => {
    if (pin.length === 6) submit();
  }, [pin, submit]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (submittingRef.current) return;
      if (/^[0-9]$/.test(event.key)) {
        event.preventDefault();
        handleDigit(event.key);
      } else if (event.key === "Backspace") {
        event.preventDefault();
        handleBackspace();
      } else if (event.key === "Escape") {
        handleClear();
      } else if (event.key === "Enter") {
        event.preventDefault();
        submit();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleDigit, handleBackspace, handleClear, submit]);

  const filled = pin.length;

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background-50 px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center">
          <BrandLogo size={88} className="mb-5" />
          <p className="text-xs font-semibold uppercase tracking-[0.24em] text-primary-600">
            {settings.company_name || BRAND_NAME}
          </p>
          <h1 className="font-heading mt-2 text-xl font-bold text-foreground-950">
            Acceso de cajero
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">Ingresa tu PIN de 6 dígitos</p>
        </div>

        <div className={shake ? "animate-shake" : ""}>
          <div className="mt-7 flex items-center justify-center gap-3">
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <span
                key={index}
                className={`h-3.5 w-3.5 rounded-full transition-all duration-200 ${
                  index < filled
                    ? "scale-110 bg-primary-500"
                    : "border-2 border-background-300 bg-transparent"
                }`}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex h-6 items-center justify-center">
          {error ? (
            <p className="flex items-center gap-1.5 text-sm font-medium text-primary-600">
              <i className="ri-error-warning-line text-base"></i>
              {error}
            </p>
          ) : submitting ? (
            <p className="flex items-center gap-1.5 text-sm text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-base"></i>
              Verificando...
            </p>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-3">
          {KEYS.map((key) => (
            <button
              key={key}
              type="button"
              disabled={submitting}
              onClick={() => handleDigit(key)}
              className="font-heading flex h-16 items-center justify-center rounded-lg border border-background-200 bg-background-50 text-2xl font-semibold text-foreground-900 transition-transform hover:bg-background-100 active:scale-95 disabled:opacity-50"
            >
              {key}
            </button>
          ))}
          <button
            type="button"
            disabled={submitting}
            onClick={handleClear}
            className="flex h-16 items-center justify-center rounded-lg border border-background-200 bg-background-50 text-sm font-semibold text-foreground-600 transition-transform hover:bg-background-100 active:scale-95 disabled:opacity-50"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => handleDigit("0")}
            className="font-heading flex h-16 items-center justify-center rounded-lg border border-background-200 bg-background-50 text-2xl font-semibold text-foreground-900 transition-transform hover:bg-background-100 active:scale-95 disabled:opacity-50"
          >
            0
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleBackspace}
            className="flex h-16 items-center justify-center rounded-lg border border-background-200 bg-background-50 text-foreground-600 transition-transform hover:bg-background-100 active:scale-95 disabled:opacity-50"
          >
            <i className="ri-delete-back-2-line text-xl"></i>
          </button>
        </div>

        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="mt-4 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          <i className="ri-login-box-line text-base"></i>
          Ingresar
        </button>

        <p className="mt-4 text-center text-xs text-foreground-400">
          Puedes usar el teclado físico: números, Enter y Backspace.
        </p>

        <Link
          to="/login"
          className="mt-6 flex items-center justify-center gap-2 text-sm font-medium text-foreground-600 hover:text-foreground-900"
        >
          <i className="ri-arrow-left-line text-base"></i>
          Volver al acceso administrativo
        </Link>
      </div>
    </div>
  );
}